import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import classnames from 'classnames';

import {
  Loader,
  Tooltip,
  OldTabProps,
  Menu,
  createMenuItems,
  CreateMenuItemsProps,
  Tabs,
  ConfirmModal,
} from '@Components';
import { ProjectItem } from '@Components/FundManagement/ProjectItem';
import { CopyToClipboard } from '@Components/CopyToClipboard/CopyToClipboard';
import {
  Match,
  Project,
  SelectionStatusEnum,
  Selection,
  Fund,
  FundStatusEnum,
  FundVotingConfiguration,
  FundGivingEngine,
  GivingEnginePaymentPartEnum,
} from '@Types';
import { getYoutubeThumbnailUrl } from '@Helpers/getYoutubeThumbnailUrl';
import { getFundPath } from '@Helpers';
import { ApiErrorObject } from 'Types/Errors';
import { useMarkSelectionsAsPaidForPaymentsExternal } from '@Hooks/Api/useMarkSelectionsAsPaidForPaymentsExternal';
import * as Sentry from '@sentry/nextjs';
import styles from './FundManagement.module.scss';
import { Button, Pagination, Tile } from '@Components/common';
import { useUserContext, useNotificationsContext } from '@Contexts';
import { formatDate } from '@Helpers/formatDate';
import {
  RiPencilFill,
  RiEyeFill,
  RiBarChartFill,
  RiArrowDownSFill,
  RiSettings3Line,
  RiDeleteBinLine,
  RiCalendarLine,
  RiMoneyPoundCircleLine,
  RiArrowLeftLine,
} from 'react-icons/ri';

import { VotingConfigurationModal } from './VotingConfigurationModal';
import { VotingLinkModal } from './VotingLinkModal';
import { PaginatedResponse } from '@Hooks/usePaginatedRequest';
import { FundSelectionsDataResponse } from '@Hooks/Api/useGetFundSelectionsData';

import { Routes } from '@App/paths';
import { RoutingPaths } from '@App/paths';

import { AFRightUpIcon } from '@Components/Icons/AFRightUpIcon';
import { ArchiveFundModal } from '@Components/common/Modal/ArchiveFundModal';
import {
  TotalizerCard,
  TotalizerProps,
} from '@Components/FunderImpactDashboard/Components/TotalizerCard';

export enum VotingModalStatus {
  // We show the voting configuration modal, followed by the sharing link modal, but never both.
  // Use an enum to manage which one is open.
  'CONFIGURATION',
  'SHARING',
  'CLOSED',
}

interface FundManagementProps {
  fundId: number;
  fundData: Fund;
  tabs: OldTabProps[];
  currentTab: number;
  setCurrentTab: (tabIndex: number) => void;
  isMatchesListLoading: boolean;
  isSelectionsListLoading: boolean;
  shortlistPaginatedResponse: PaginatedResponse<Selection>;
  shortlistSelectionsData?: FundSelectionsDataResponse;
  isShortlistSelectionsDataLoading: boolean;
  isFundedProjectsLoading: boolean;
  invitedSelections?: Selection[];
  dueDiligenceProjectsList: Project[];
  isDueDiligenceLoading: boolean;
  awaitingPaymentProjectsList: Project[];
  isAwaitingPaymentLoading: boolean;
  fundedProjectsList: Project[];
  completedProjectsList: Project[];
  isCompletedProjectsLoading: boolean;
  moveProjectToShortlist: (projectId: number, projectName: string) => Promise<void>;
  rejectMatchedProject: (matchId: number, projectName: string) => Promise<void>;
  removeProjectFromShortlist: (selectionId: number, projectName: string) => Promise<void>;
  inviteProjectToFund: (selectionId: number, projectName: string) => Promise<void>;
  onVotingConfigurationSubmit: (data: FundVotingConfiguration) => void;
  votingModalStatus: VotingModalStatus;
  setVotingModalStatus: React.Dispatch<React.SetStateAction<VotingModalStatus>>;
  votingConfigurationUpdateErrors?: ApiErrorObject;
  votingConfigurationUpdateLoading: boolean;
  matchesPaginatedResponse: PaginatedResponse<Match>;
  fundGivingEngine?: FundGivingEngine;
  isFundGivingEngineLoading: boolean;
  getSelectionIdForProject: (projectId: number) => number | undefined;
  refreshAwaitingPaymentProjects: (fundId: number, options?: any) => void;
  refreshFundedProjects: (fundId: number, options?: any) => void;
}

export const FundManagement: React.FC<FundManagementProps> = ({
  fundId,
  fundData,
  tabs,
  currentTab,
  setCurrentTab,
  isMatchesListLoading,
  isSelectionsListLoading,
  shortlistPaginatedResponse,
  shortlistSelectionsData,
  isShortlistSelectionsDataLoading,
  isFundedProjectsLoading,
  invitedSelections,
  dueDiligenceProjectsList,
  isDueDiligenceLoading,
  awaitingPaymentProjectsList,
  isAwaitingPaymentLoading,
  fundedProjectsList,
  completedProjectsList,
  isCompletedProjectsLoading,
  moveProjectToShortlist,
  rejectMatchedProject,
  removeProjectFromShortlist,
  inviteProjectToFund,
  onVotingConfigurationSubmit,
  votingModalStatus,
  setVotingModalStatus,
  votingConfigurationUpdateErrors,
  votingConfigurationUpdateLoading,
  matchesPaginatedResponse,
  fundGivingEngine,
  isFundGivingEngineLoading,
  getSelectionIdForProject,
  refreshAwaitingPaymentProjects,
  refreshFundedProjects,
}) => {
  const { t } = useTranslation('fund');
  const [loadingProjectId, setLoadingProjectId] = useState<number>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isConfirmInviteModalOpen, setIsConfirmInviteModalOpen] = useState(false);
  const [isConfirmSetProjectAsPaidModalOpen, setIsConfirmSetProjectAsPaidModalOpen] =
    useState(false);
  const [setProjectAsPaidProjectData, setSetProjectAsPaidProjectData] = useState<{
    selectionId: number;
    projectName: string;
    projectId: number;
  } | null>(null);
  const { status, details } = fundData;
  const { name: fundName, starting_amount: fundAmount } = details;

  const anchorEl = React.useRef<HTMLButtonElement>(null);
  const { userData } = useUserContext();
  const { success: successNotification } = useNotificationsContext();
  const [markSelectionsAsPaidForPaymentsExternal, markSelectionsAsPaidForPaymentsExternalResponse] =
    useMarkSelectionsAsPaidForPaymentsExternal();

  const { page: matchList } = matchesPaginatedResponse;
  const { page: shortlistedSelections } = shortlistPaginatedResponse;

  // Use total funding requested from selections data endpoint
  const fundingRequestedShortlist = useMemo(() => {
    if (!shortlistSelectionsData?.total_funding) return 0;
    return shortlistSelectionsData.total_funding;
  }, [shortlistSelectionsData]);

  // Calculate funding allocated based on all projects in the funding pipeline
  // This includes: offered funding (and not rejected ), due diligence, awaiting payment, funded, complete, and survey done
  const fundingAllocated = useMemo(() => {
    if (
      !invitedSelections ||
      !dueDiligenceProjectsList ||
      !awaitingPaymentProjectsList ||
      !fundedProjectsList ||
      !completedProjectsList
    ) {
      return undefined;
    }

    const allProjects = [
      ...invitedSelections
        .filter((selection) => selection.status !== SelectionStatusEnum.INVITATION_REJECTED)
        .map((selection) => selection.project),
      ...dueDiligenceProjectsList,
      ...awaitingPaymentProjectsList,
      ...fundedProjectsList,
      ...completedProjectsList,
    ];

    return allProjects.reduce((acc, project) => acc + Number(project.details.amount), 0);
  }, [
    invitedSelections,
    dueDiligenceProjectsList,
    awaitingPaymentProjectsList,
    fundedProjectsList,
    completedProjectsList,
  ]);

  // Create totalizer cards for fund stats
  const totalizers: TotalizerProps[] = [
    {
      title: t('management.totalizers.fund_application_window'),
      amount: `${formatDate(fundData.details.launch_from)} - ${formatDate(
        fundData.details.deadline_to,
      )}`,
      icon: <RiCalendarLine size={18} />,
    },
    {
      title: t('management.totalizers.fund_selection_deadline'),
      amount: formatDate(fundData.details.selection_deadline),
      icon: <RiCalendarLine size={18} />,
    },
    {
      title: t('management.totalizers.funding_shortlisted'),
      amount: `${t('common:currency_format', { amount: fundingRequestedShortlist || 0 })} / ${t(
        'common:currency_format',
        { amount: Number(fundAmount) || 0 },
      )}`,
      icon: <RiMoneyPoundCircleLine size={18} />,
    },
    {
      title: t('management.totalizers.funding_allocated'),
      amount: `${t('common:currency_format', { amount: fundingAllocated || 0 })} / ${t(
        'common:currency_format',
        { amount: Number(fundAmount) || 0 },
      )}`,
      icon: <RiMoneyPoundCircleLine size={18} />,
    },
  ];

  const createHandleMoveProjectToShortlist = useCallback(
    (projectId: number, projectName: string) => () => {
      setLoadingProjectId(projectId);
      moveProjectToShortlist(projectId, projectName).finally(() => {
        setLoadingProjectId(undefined);
      });
    },
    [moveProjectToShortlist],
  );

  const createHandleRejectMatchedProject = useCallback(
    (matchId: number, projectName: string, projectId: number) => () => {
      setLoadingProjectId(projectId);
      rejectMatchedProject(matchId, projectName).finally(() => {
        setLoadingProjectId(undefined);
      });
    },
    [rejectMatchedProject],
  );

  const createHandleRemoveProjectFromShortlist = useCallback(
    (selectionId: number, projectName: string, projectId: number) => () => {
      setLoadingProjectId(projectId);
      removeProjectFromShortlist(selectionId, projectName).finally(() => {
        setLoadingProjectId(undefined);
      });
    },
    [removeProjectFromShortlist],
  );

  const createHandleInviteProjectToFund = useCallback(
    (selectionId: number, projectName: string, projectId: number) => () => {
      setLoadingProjectId(projectId);
      inviteProjectToFund(selectionId, projectName).finally(() => {
        setLoadingProjectId(undefined);
      });
    },
    [inviteProjectToFund],
  );

  const userCanManage = fundData.user_can_manage;
  const userCanEdit =
    status === FundStatusEnum.DRAFT || status === FundStatusEnum.AWAITING_APPROVAL;
  const votingConfiguration = fundData?.voting_configuration;
  const userCanArchive = fundData?.user_can_archive;

  const votingEnabled =
    [FundStatusEnum.LIVE, FundStatusEnum.CLOSED].includes(fundData.status) &&
    userData?.selection_voting_enabled;

  const handleVotingModalClose = () => {
    setVotingModalStatus(VotingModalStatus.CLOSED);
  };

  const onMenuOpen = () => {
    setIsMenuOpen(true);
  };

  const onMenuClose = () => {
    setIsMenuOpen(false);
  };

  const [inviteProjectData, setInviteProjectData] = useState<{
    selectionId: number;
    projectName: string;
    projectId: number;
  } | null>(null);

  const showConfirmInviteModal = useCallback(
    (selectionId: number, projectName: string, projectId: number) => {
      setInviteProjectData({ selectionId, projectName, projectId });
      setIsConfirmInviteModalOpen(true);
    },
    [],
  );

  const hideConfirmInviteModal = useCallback(() => {
    setIsConfirmInviteModalOpen(false);
    setInviteProjectData(null);
  }, []);

  const handleConfirmInvite = useCallback(() => {
    if (inviteProjectData) {
      hideConfirmInviteModal();
      createHandleInviteProjectToFund(
        inviteProjectData.selectionId,
        inviteProjectData.projectName,
        inviteProjectData.projectId,
      )();
    }
  }, [inviteProjectData, hideConfirmInviteModal, createHandleInviteProjectToFund]);

  const showConfirmSetProjectAsPaid = useCallback(
    (selectionId: number | undefined, projectName: string, projectId: number) => {
      if (!selectionId) return;
      setSetProjectAsPaidProjectData({
        selectionId: selectionId,
        projectName: projectName,
        projectId: projectId,
      });
      setIsConfirmSetProjectAsPaidModalOpen(true);
    },
    [],
  );

  const hideConfirmSetProjectAsPaidModal = useCallback(() => {
    setIsConfirmSetProjectAsPaidModalOpen(false);
    setSetProjectAsPaidProjectData(null);
  }, [setSetProjectAsPaidProjectData]);

  const handleSetProjectAsPaid = useCallback(() => {
    if (!setProjectAsPaidProjectData) return;

    markSelectionsAsPaidForPaymentsExternal(fundId, [setProjectAsPaidProjectData.selectionId])
      .then(() => {
        successNotification(t('management.project_marked_as_paid_successfully'));
        hideConfirmSetProjectAsPaidModal();

        // Refresh the lists to show the project moving to the funded tab
        refreshAwaitingPaymentProjects(fundId, { detailedProjectStatus: 'AWAITING_PAYMENT' });
        refreshFundedProjects(fundId, { detailedProjectStatus: 'FUNDED' });
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, [
    setProjectAsPaidProjectData,
    markSelectionsAsPaidForPaymentsExternal,
    fundId,
    successNotification,
    t,
    hideConfirmSetProjectAsPaidModal,
    refreshAwaitingPaymentProjects,
    refreshFundedProjects,
  ]);

  const dropDownMenuItems: CreateMenuItemsProps['items'] = [
    ...(userCanEdit
      ? [
          {
            label: t('management.edit'),
            menuItemProps: {
              onClick: () => {
                window.location.href = Routes.FUND_EDIT(fundId);
                onMenuClose();
              },
            },
            icon: <RiPencilFill />,
          },
        ]
      : []),
    {
      label: t('management.view'),
      menuItemProps: {
        onClick: () => {
          window.open(getFundPath(fundId, fundData.access_code), '_blank');
          onMenuClose();
        },
      },
      icon: <RiEyeFill />,
    },
    ...(userCanArchive
      ? [
          {
            label: t('management.delete'),
            icon: <RiDeleteBinLine className={styles.dangerColour} />,
            menuItemProps: {
              onClick: () => {
                setIsArchiveModalOpen(true);
                onMenuClose();
              },
              className: styles.dangerColour,
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <div className={styles.headerWrapper}>
        <Button
          buttonType="text"
          size="small"
          startIcon={<RiArrowLeftLine size={12} />}
          className={styles.backButton}
          to={Routes.MY_FUNDS}
        >
          {t('management.return_to_all_funds')}
        </Button>
        <div className={styles.headerContent}>
          <div>
            <h1 className={styles.fundName}>{fundName}</h1>
            <div className={classnames(styles[status], styles.statusContainer)}>
              <AFRightUpIcon className={classnames(styles[status], styles.icon)} />
              {t(`dashboard:funder.fund_status.${status}`)}
            </div>
          </div>
          <div className={styles.managementButtons}>
            <Tooltip
              title={
                completedProjectsList.length === 0 ? (t('management.no_results_yet') as string) : ''
              }
              placement="top"
            >
              <span>
                <Button
                  buttonType="secondary"
                  to={Routes.FUND_IMPACT(fundId)}
                  startIcon={<RiBarChartFill />}
                  size="small"
                  disabled={completedProjectsList.length === 0}
                >
                  {t('management.view_impact')}
                </Button>
              </span>
            </Tooltip>
            <Button
              buttonType="secondary"
              onClick={onMenuOpen}
              ref={anchorEl}
              endIcon={<RiArrowDownSFill />}
              startIcon={<RiSettings3Line />}
              size="small"
            >
              {t('management.settings')}
            </Button>
            <Menu open={isMenuOpen} anchorEl={anchorEl.current} onClose={onMenuClose}>
              {createMenuItems({ items: dropDownMenuItems })}
            </Menu>
            <ArchiveFundModal
              fundId={fundId}
              isOpen={isArchiveModalOpen}
              onClose={() => setIsArchiveModalOpen(false)}
              onSuccess={() => {
                window.location.href = RoutingPaths.DASHBOARD_GROUP;
              }}
            />
          </div>
        </div>
      </div>

      <div className={styles.totalizersRow}>
        {totalizers.map((totalizer, index) => (
          <TotalizerCard totalizer={totalizer} key={index} />
        ))}
      </div>

      <Tile>
        <div className={styles.contentContainer}>
          <Tabs
            tabs={tabs}
            currentTab={currentTab}
            onTabChange={setCurrentTab}
            className={styles.tabs}
            seperator
          />
          {currentTab === 0 && (
            <>
              {isMatchesListLoading ? (
                <Loader />
              ) : (
                <>
                  {matchList && matchList.length > 0 ? (
                    <>
                      <div className={styles.projectList}>
                        {matchList.map(
                          ({ project, is_funded, group_nomination_count, id: matchId }) => (
                            <ProjectItem
                              key={project.id}
                              projectId={project.id}
                              projectName={project.details.name}
                              organizationName={project.group_profile.name}
                              amount={t(`common:currency_format`, {
                                amount: project.details.amount,
                              })}
                              beneficiaries={project.delivery_plan?.beneficiaries}
                              hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                              deliveryDate={
                                project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                                  ? t('management.delivery_timeframe_text_week', {
                                      count: project.delivery_plan?.timeframe_estimate_number,
                                    })
                                  : t('management.delivery_timeframe_text_month', {
                                      count: project.delivery_plan?.timeframe_estimate_number,
                                    })
                              }
                              projectImage={project.written_pitch?.image}
                              videoThumbnail={getYoutubeThumbnailUrl(
                                project.video_pitch?.video_url_1,
                              )}
                              {...(userCanManage && {
                                primaryButtonLabel: t('management.shortlist'),
                                primaryButton: {
                                  onClick: createHandleMoveProjectToShortlist(
                                    project.id,
                                    project.details.name,
                                  ),
                                  buttonType: 'success',
                                  size: 'small',
                                },
                                secondaryButtonLabel: t('management.reject'),
                                secondaryButton: {
                                  onClick: createHandleRejectMatchedProject(
                                    matchId,
                                    project.details.name,
                                    project.id,
                                  ),
                                  buttonType: 'danger',
                                  size: 'small',
                                },
                              })}
                              isFunded={is_funded}
                              nominationCount={group_nomination_count}
                              surveyCompleted={false}
                            />
                          ),
                        )}
                      </div>
                      <Pagination paginatedResponse={matchesPaginatedResponse} />
                    </>
                  ) : (
                    <div className={styles.noProjects}>{t('management.no_matches_found')}</div>
                  )}
                </>
              )}
            </>
          )}

          {currentTab === 1 && (
            <>
              {isSelectionsListLoading ? (
                <Loader />
              ) : (
                <>
                  {shortlistedSelections && shortlistedSelections.length > 0 ? (
                    <>
                      {votingEnabled && (
                        <>
                          <div className={styles.votingControls}>
                            {votingConfiguration && !votingConfiguration.deadline_passed && (
                              <CopyToClipboard
                                buttonText={t('management.copy_voting_link')}
                                successText={t('management.copy_voting_link_success')}
                                valueToCopy={votingConfiguration.sharing_link}
                              />
                            )}
                            {(!votingConfiguration || votingConfiguration.is_editable) &&
                              userCanManage && (
                                <div>
                                  <Button
                                    buttonType="tertiary"
                                    onClick={() => {
                                      setVotingModalStatus(VotingModalStatus.CONFIGURATION);
                                    }}
                                  >
                                    {votingConfiguration ? (
                                      <>{t('management.view_voting_configuration')}</>
                                    ) : (
                                      <>{t('management.enable_voting')}</>
                                    )}
                                  </Button>
                                </div>
                              )}
                          </div>
                          {votingConfiguration && (
                            <div className={styles.voteCount}>
                              {t('management.vote_count', {
                                count: votingConfiguration.total_votes,
                              })}
                              {t('management.voter_count', {
                                count: votingConfiguration.total_voters,
                              })}
                            </div>
                          )}
                        </>
                      )}

                      <div className={styles.projectList}>
                        {shortlistedSelections.map(
                          ({
                            project,
                            id: selectionId,
                            vote_count: voteCount,
                            group_nomination_count,
                            status: selectionStatus,
                          }) => (
                            <ProjectItem
                              key={project.id}
                              projectId={project.id}
                              projectName={project.details.name}
                              organizationName={project.group_profile.name}
                              amount={t(`common:currency_format`, {
                                amount: project.details.amount,
                              })}
                              beneficiaries={project.delivery_plan?.beneficiaries}
                              hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                              deliveryDate={
                                project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                                  ? t('management.delivery_timeframe_text_week', {
                                      count: project.delivery_plan?.timeframe_estimate_number,
                                    })
                                  : t('management.delivery_timeframe_text_month', {
                                      count: project.delivery_plan?.timeframe_estimate_number,
                                    })
                              }
                              projectImage={project.written_pitch?.image}
                              videoThumbnail={getYoutubeThumbnailUrl(
                                project.video_pitch?.video_url_1,
                              )}
                              selectionStatus={selectionStatus}
                              {...(userCanManage && {
                                primaryButtonLabel: t('management.offer_funding_button'),
                                primaryButton: {
                                  onClick: () =>
                                    showConfirmInviteModal(
                                      selectionId,
                                      project.details.name,
                                      project.id,
                                    ),
                                  buttonType: 'success',
                                  size: 'small',
                                },
                                secondaryButtonLabel: t('management.remove_from_shortlist_button'),
                                secondaryButton: {
                                  onClick: createHandleRemoveProjectFromShortlist(
                                    selectionId,
                                    project.details.name,
                                    project.id,
                                  ),
                                  buttonType: 'secondary',
                                  size: 'small',
                                },
                              })}
                              voteCount={voteCount}
                              showVotes={votingConfiguration != null}
                              nominationCount={group_nomination_count}
                              surveyCompleted={false}
                            />
                          ),
                        )}
                      </div>
                      <Pagination paginatedResponse={shortlistPaginatedResponse} />
                    </>
                  ) : (
                    <div className={styles.noProjects}>
                      {t('management.no_shortlisted_projects')}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {currentTab === 2 && (
            <>
              {isSelectionsListLoading ? (
                <Loader />
              ) : (
                <>
                  {invitedSelections && invitedSelections.length > 0 ? (
                    <>
                      <div className={styles.projectList}>
                        {invitedSelections.map(
                          ({ project, status: selectionStatus, group_nomination_count }) => (
                            <ProjectItem
                              key={project.id}
                              projectId={project.id}
                              projectName={project.details.name}
                              organizationName={project.group_profile.name}
                              amount={t(`common:currency_format`, {
                                amount: project.details.amount,
                              })}
                              beneficiaries={project.delivery_plan?.beneficiaries}
                              hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                              deliveryDate={
                                project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                                  ? t('management.delivery_timeframe_text_week', {
                                      count: project.delivery_plan?.timeframe_estimate_number,
                                    })
                                  : t('management.delivery_timeframe_text_month', {
                                      count: project.delivery_plan?.timeframe_estimate_number,
                                    })
                              }
                              projectImage={project.written_pitch?.image}
                              videoThumbnail={getYoutubeThumbnailUrl(
                                project.video_pitch?.video_url_1,
                              )}
                              selectionStatus={selectionStatus}
                              nominationCount={group_nomination_count}
                              surveyCompleted={false}
                            />
                          ),
                        )}
                      </div>
                    </>
                  ) : (
                    <div className={styles.noProjects}>{t('management.no_funding_offers')}</div>
                  )}
                </>
              )}
            </>
          )}

          {currentTab === 3 && (
            <>
              {isDueDiligenceLoading ? (
                <Loader />
              ) : (
                <>
                  {dueDiligenceProjectsList && dueDiligenceProjectsList.length > 0 ? (
                    <div className={styles.projectList}>
                      {dueDiligenceProjectsList.map((project) => (
                        <ProjectItem
                          key={project.id}
                          projectId={project.id}
                          projectName={project.details.name}
                          organizationName={project.group_profile.name}
                          amount={t(`common:currency_format`, {
                            amount: project.details.amount,
                          })}
                          beneficiaries={project.delivery_plan?.beneficiaries}
                          hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                          deliveryDate={
                            project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                              ? t('management.delivery_timeframe_text_week', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                              : t('management.delivery_timeframe_text_month', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                          }
                          projectImage={project.written_pitch?.image}
                          videoThumbnail={getYoutubeThumbnailUrl(project.video_pitch?.video_url_1)}
                          surveyCompleted={false}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noProjects}>
                      {t('management.no_due_diligence_projects')}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {currentTab === 4 && (
            <>
              {isAwaitingPaymentLoading ? (
                <Loader />
              ) : (
                <>
                  {awaitingPaymentProjectsList && awaitingPaymentProjectsList.length > 0 ? (
                    <div className={styles.projectList}>
                      {awaitingPaymentProjectsList.map((project, selectionId) => (
                        <ProjectItem
                          key={project.id}
                          projectId={project.id}
                          projectName={project.details.name}
                          organizationName={project.group_profile.name}
                          amount={t(`common:currency_format`, {
                            amount: project.details.amount,
                          })}
                          beneficiaries={project.delivery_plan?.beneficiaries}
                          hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                          deliveryDate={
                            project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                              ? t('management.delivery_timeframe_text_week', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                              : t('management.delivery_timeframe_text_month', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                          }
                          projectImage={project.written_pitch?.image}
                          videoThumbnail={getYoutubeThumbnailUrl(project.video_pitch?.video_url_1)}
                          surveyCompleted={false}
                          primaryButton={
                            fundGivingEngine?.payment.part === GivingEnginePaymentPartEnum.EXTERNAL
                              ? {
                                  onClick: () =>
                                    showConfirmSetProjectAsPaid(
                                      getSelectionIdForProject(project.id),
                                      project.details.name,
                                      project.id,
                                    ),
                                  buttonType: 'success',
                                  size: 'small',
                                }
                              : undefined
                          }
                          primaryButtonLabel={t('management.mark_project_as_paid_button')}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noProjects}>
                      {t('management.no_awaiting_payment_projects')}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {currentTab === 5 && (
            <>
              {isFundedProjectsLoading ? (
                <Loader />
              ) : (
                <>
                  {fundedProjectsList && fundedProjectsList.length > 0 ? (
                    <div className={styles.projectList}>
                      {fundedProjectsList.map((project) => (
                        <ProjectItem
                          key={project.id}
                          projectId={project.id}
                          projectName={project.details.name}
                          organizationName={project.group_profile.name}
                          amount={t(`common:currency_format`, {
                            amount: project.details.amount,
                          })}
                          beneficiaries={project.delivery_plan?.beneficiaries}
                          hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                          deliveryDate={
                            project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                              ? t('management.delivery_timeframe_text_week', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                              : t('management.delivery_timeframe_text_month', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                          }
                          projectImage={project.written_pitch?.image}
                          videoThumbnail={getYoutubeThumbnailUrl(project.video_pitch?.video_url_1)}
                          surveyCompleted={false}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noProjects}>{t('management.no_funded_projects')}</div>
                  )}
                </>
              )}
            </>
          )}

          {currentTab === 6 && (
            <>
              {isCompletedProjectsLoading ? (
                <Loader />
              ) : (
                <>
                  {completedProjectsList && completedProjectsList.length > 0 ? (
                    <div className={styles.projectList}>
                      {completedProjectsList.map((project) => (
                        <ProjectItem
                          key={project.id}
                          projectId={project.id}
                          projectName={project.details.name}
                          organizationName={project.group_profile.name}
                          amount={t(`common:currency_format`, {
                            amount: project.details.amount,
                          })}
                          beneficiaries={project.delivery_plan?.beneficiaries}
                          hasVolunteeringOpportunity={project.has_volunteering_opportunity}
                          deliveryDate={
                            project.delivery_plan?.timeframe_estimate_unit === 'WEEK'
                              ? t('management.delivery_timeframe_text_week', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                              : t('management.delivery_timeframe_text_month', {
                                  count: project.delivery_plan?.timeframe_estimate_number,
                                })
                          }
                          projectImage={project.written_pitch?.image}
                          videoThumbnail={getYoutubeThumbnailUrl(project.video_pitch?.video_url_1)}
                          surveyCompleted={project.survey_completed}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noProjects}>{t('management.no_completed_projects')}</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Tile>

      <ConfirmModal
        isOpen={isConfirmInviteModalOpen}
        onClose={hideConfirmInviteModal}
        onConfirm={handleConfirmInvite}
      >
        {inviteProjectData && (
          <Trans
            ns="fund"
            i18nKey="management.confirm_invite_to_fund"
            components={[<b />]}
            values={{ projectName: inviteProjectData.projectName }}
          />
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={isConfirmSetProjectAsPaidModalOpen}
        onClose={hideConfirmSetProjectAsPaidModal}
        onConfirm={handleSetProjectAsPaid}
      >
        {setProjectAsPaidProjectData && (
          <Trans
            ns="fund"
            i18nKey="management.confirm_set_project_as_paid"
            components={[<b />]}
            values={{ projectName: setProjectAsPaidProjectData.projectName }}
          />
        )}
      </ConfirmModal>

      <VotingConfigurationModal
        isOpen={votingModalStatus == VotingModalStatus.CONFIGURATION}
        onClose={handleVotingModalClose}
        votingConfiguration={votingConfiguration}
        onSubmit={onVotingConfigurationSubmit}
        votingConfigurationUpdateErrors={votingConfigurationUpdateErrors}
        votingConfigurationUpdateLoading={votingConfigurationUpdateLoading}
      />
      {votingConfiguration && (
        <VotingLinkModal
          isOpen={votingModalStatus == VotingModalStatus.SHARING}
          onClose={handleVotingModalClose}
          votingConfiguration={votingConfiguration}
        />
      )}
    </>
  );
};
